import { FunctionComponent, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as FullscreenIconSvg } from 'src/icons/full-screen.svg';
import { ReactComponent as FullscreenExitIconSvg } from 'src/icons/full-screen-exit.svg';
import { ReactComponent as PlayIconSvg } from 'src/icons/play.svg';
import { ReactComponent as PauseIconSvg } from 'src/icons/pause.svg';
import { Colors } from 'src/utils/color';

const MainContainer = styled.div<{
    backgroundColor?: string;
    width?: string;
    height?: string;
}>`
    width: ${(props) => props.width || '100%'};
    height: ${(props) => props.height || '100%'};
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.backgroundColor || Colors.extra.black};
    border-radius: 4px;
    position: relative;
`;

const VideoContainer = styled.video`
    width: 100%;
    height: 100%;
`;

const ImageContainer = styled.img`
    width: 100%;
    height: 100%;
`;

const IconWrapper = styled.div`
    width: 100%;
    height: 100%;
    margin-left: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 100%;
        height: 100%;
    }
`;

const FullscreenIconWrapper = styled.div`
    width: 22px;
    height: 17px;
    position: absolute;
    right: 18px;
    bottom: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 100%;
        height: 100%;
    }
`;

const Circle = styled.div`
    width: 150px;
    height: 150px;
    border-radius: 100%;
    position: absolute;
    border: 7px solid ${Colors.theme.primary};
    padding: 34px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

interface Props {
    backgroundColor?: string;
    width?: string;
    height?: string;
    url: string | null;
}

const VideoPlayer: FunctionComponent<Props> = ({
    backgroundColor,
    width,
    height,
    url
}: Props) => {
    const [isPlay, setIsPlay] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const playPauseVideo = useCallback(() => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                setIsPlay(true);
                videoRef.current.play();
            } else {
                setIsPlay(false);
                videoRef.current.pause();
            }
        }
    }, [videoRef]);

    const urlType = url
        ? (url.split(/[#?]/)[0].split('.').pop() || '').trim()
        : null;
    const isGif = urlType?.toLowerCase().includes('gif');

    return (
        <MainContainer
            width={width}
            height={height}
            backgroundColor={backgroundColor}
        >
            {url &&
                (isGif ? (
                    <ImageContainer src={url} alt="preview" />
                ) : (
                    <VideoContainer ref={videoRef}>
                        <source src={url} type={'video/' + urlType} />
                    </VideoContainer>
                ))}

            {!isGif && (
                <Circle onClick={playPauseVideo}>
                    <IconWrapper>
                        {isPlay ? <PauseIconSvg /> : <PlayIconSvg />}
                    </IconWrapper>
                </Circle>
            )}
            {!isGif && (
                <FullscreenIconWrapper
                    onClick={() => videoRef.current?.requestFullscreen()}
                >
                    <FullscreenIconSvg />
                </FullscreenIconWrapper>
            )}
        </MainContainer>
    );
};

export default VideoPlayer;
